import type { ReactNode, Ref } from 'react';
import { Box, Card, Typography } from '@mui/material';
import { DeltaChip } from './DeltaChip';

export interface StatCardProps {
  /** Metric label; rendered UPPERCASE. */
  label: string;
  /** The primary value (pre-formatted by the caller). */
  value: ReactNode;
  /** Signed delta; when set, a {@link DeltaChip} is rendered. */
  delta?: number;
  /** Direction that reads as good for the delta tint. */
  goodDirection?: 'up' | 'down';
  /** Unit for the delta magnitude (defaults to `%`). */
  deltaSuffix?: string;
  /** Small caption beside the delta (e.g. "vs last month"). */
  secondary?: ReactNode;
  /** Draws a colored left rail (e.g. error red for an over-budget metric). */
  accentColor?: string;
  /** Optional trailing visual (icon badge). Charts are out of scope here. */
  icon?: ReactNode;
  ref?: Ref<HTMLDivElement>;
}

/**
 * KPI tile: uppercase label, large tabular value, and an optional delta chip.
 *
 * Charts (sparklines) are intentionally NOT rendered here — the dashboard slice
 * (09-dashboard) picks the chart library and adds them later. This card is
 * value-plus-delta only.
 */
export function StatCard({
  label,
  value,
  delta,
  goodDirection = 'up',
  deltaSuffix,
  secondary,
  accentColor,
  icon,
  ref,
}: StatCardProps) {
  return (
    <Card
      ref={ref}
      variant="outlined"
      sx={{
        p: '18px 20px',
        minHeight: 120,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 1.5,
        ...(accentColor ? { borderLeft: `3px solid ${accentColor}` } : {}),
      }}
    >
      <Typography
        component="div"
        title={label}
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.6px',
          textTransform: 'uppercase',
          color: 'text.secondary',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </Typography>

      <Box
        sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="div"
            sx={{
              fontSize: 30,
              fontWeight: 700,
              lineHeight: 1.05,
              letterSpacing: '-0.01em',
              color: 'text.primary',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {value}
          </Typography>
          {(delta !== undefined || secondary) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              {delta !== undefined && (
                <DeltaChip delta={delta} goodDirection={goodDirection} suffix={deltaSuffix} />
              )}
              {secondary && (
                <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {secondary}
                </Typography>
              )}
            </Box>
          )}
        </Box>
        {icon && <Box sx={{ flexShrink: 0, display: 'inline-flex' }}>{icon}</Box>}
      </Box>
    </Card>
  );
}
